import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
// material
import {
  Box,
  Grid,
  Typography,
  Modal,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { parseValidationErrorMessage } from '../../../utils/Utilities';
import { useAuth } from '../../customContextProviders/AuthContext';
import { AddNewUserToNox, UpdateUserToNox } from '../../../network/User';
import { SnackBarAlert } from '../../HelperComponents';

// ----------------------------------------------------------------------

const style = {
  width: '100%',
  maxWidth: { sm: '90%', xs: '90%', md: 600 },
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '30%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 3,
  borderRadius: 1
};

const UserSchema = Yup.object().shape({
  userName: Yup.string().required('Required'),
  userPassword: Yup.string().when('selectedUser', {
    is: true,
    then: Yup.string(),
    otherwise: Yup.string().required('Password is required')
  }),
  userPhone: Yup.string().required('Required'),
  userEmail: Yup.string().required('Required'),
  userRole: Yup.string().required('Required')
});

AddUserToOrganizationModal.propTypes = {
  opneAssignedModal: PropTypes.bool,
  handleClose: PropTypes.func
};

export default function AddUserToOrganizationModal({
  opneAssignedModal,
  handleClose,
  selectedUser,
  onAction,
  headOfficesList
}) {
  const { sessionUserData, organizationAudioCubeTerritories, getAudioCubeOrganizationTerritories } =
    useAuth();
  const location = useLocation();

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const addUserFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      userName: selectedUser?.name || '',
      userEmail: selectedUser?.email || '',
      userPassword: '',
      userPhone: selectedUser?.phone || '',
      userRole: selectedUser?.role || '',
      employeeId: selectedUser?.username || '',
      headOfficeName: headOfficesList[0]?.name || '',
      selectedUser: !!selectedUser // New field to track if it's an editing situation
    },
    validationSchema: UserSchema,
    onSubmit: () => {
      const payload = Object.fromEntries(
        Object.entries({
          email: addUserFormik?.values?.userEmail,
          password: addUserFormik?.values?.userPassword,
          name: addUserFormik?.values?.userName,
          phone: addUserFormik?.values?.userPhone,
          role: addUserFormik?.values?.userRole,
          username: addUserFormik?.values?.employeeId,
          organizationId: sessionUserData?.user?.organizationId,
          headOffice: values?.headOfficeName
        }).filter(([_, v]) => v !== undefined && v !== null && v !== '')
      );

      const apiCall = selectedUser?.id
        ? UpdateUserToNox(selectedUser?.id, payload)
        : AddNewUserToNox(payload);

      apiCall
        .then((_res) => {
          addUserFormik.setSubmitting(false);
          handleClose();
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: selectedUser
              ? 'Successfully Updated'
              : `${addUserFormik?.values?.userName} successfully created!`,
            severity: 'success'
          }));
          setIsSnackBarAlertOpen(true);

          if (location?.pathname === '/dashboard/organizationusers') {
            onAction();
          }
        })
        .catch((error) => {
          addUserFormik.setSubmitting(false);
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: parseValidationErrorMessage(error),
            severity: 'error'
          }));
          setIsSnackBarAlertOpen(true);
        });
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setFieldValue } =
    addUserFormik;

  return (
    <Box>
      <Modal
        open={opneAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <FormikProvider enableReinitialize="true" value={addUserFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat' }}>
                {selectedUser?.id ? 'Update User' : 'Create User'}
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Name"
                      id="userName"
                      name="userName"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('userName')}
                      error={Boolean(touched.userName && errors.userName)}
                      helperText={touched.userName && errors.userName}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Employee ID"
                      id="employeeId"
                      name="employeeId"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('employeeId')}
                      error={Boolean(touched.employeeId && errors.employeeId)}
                      helperText={touched.employeeId && errors.employeeId}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Mobile No"
                      id="userPhone"
                      name="userPhone"
                      variant="outlined"
                      {...getFieldProps('userPhone')}
                      error={Boolean(touched.userPhone && errors.userPhone)}
                      helperText={touched.userPhone && errors.userPhone}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Email"
                      id="userEmail"
                      name="userEmail"
                      type="email"
                      variant="outlined"
                      {...getFieldProps('userEmail')}
                      error={Boolean(touched.userEmail && errors.userEmail)}
                      helperText={touched.userEmail && errors.userEmail}
                    />
                  </FormControl>
                </Grid>

                {!selectedUser?.name && (
                  <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <TextField
                        label="Password"
                        id="userPassword"
                        name="userPassword"
                        type="password"
                        variant="outlined"
                        {...getFieldProps('userPassword')}
                        error={Boolean(touched.userPassword && errors.userPassword)}
                        helperText={touched.userPassword && errors.userPassword}
                      />
                    </FormControl>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="device-location-select-label">Role </InputLabel>
                    <Select
                      labelId="user-role-select-label"
                      id="user-role-select"
                      value={addUserFormik?.values.userRole}
                      label="Role "
                      onChange={addUserFormik.handleChange}
                      {...getFieldProps('userRole')}
                      error={Boolean(touched.userRole && errors.userRole)}
                      helperText={touched.userRole && errors.userRole}
                    >
                      <MenuItem value="admin" sx={{ p: 1.5 }}>
                        {' '}
                        Admin
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <Autocomplete
                    options={headOfficesList}
                    getOptionLabel={(option) => option.name || ''}
                    value={headOfficesList?.find((o) => o.name === values.headOfficeName) || null}
                    onChange={(e, newValue) => {
                      const selectedId = newValue ? newValue.id : '';
                      setFieldValue('headOfficeId', selectedId);
                      setFieldValue('headOfficeName', newValue ? newValue.name : '');
                      // if (selectedId) {
                      //   fetchZones(selectedId);
                      // }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Head Office"
                        error={Boolean(touched.headOfficeId && errors.headOfficeId)}
                        helperText={touched.headOfficeId && errors.headOfficeId}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Grid> */}
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  loading={addUserFormik.isSubmitting}
                  sx={{ mt: 2, height: '40px' }}
                  variant="contained"
                  type="submit"
                  size="large"
                >
                  {selectedUser?.id ? 'Update' : 'Create'}
                </LoadingButton>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>
      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </Box>
  );
}
